import {
  IPaymentMethodContactSchema,
  ISharedInvoicePaymentReq,
  IShareInvoiceGenerateSetupIntentResp,
  IShareInvoiceRespSchema,
  PaymentMethodSummaryResponse,
  QuoteShareContact,
  QuoteShareContactRequest,
  QuoteShareContactsForm,
  QuoteSharePaymentMethodRequest,
  QuoteShareResponse,
  RecordShareBaseParams,
  RecordShareEngagement,
  RecordShareEngagementAction,
  RecordShareEngagementSchema,
  RecordShareEntity,
  SharedInvoicePaymentExistingMethod,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { SetupIntentResult, Stripe, StripeElements } from '@stripe/stripe-js';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosHeaders } from 'axios';
import { useSetAtom } from 'jotai';
import { logger } from '../services/logger';
import Sentry from '../services/sentry';
import { useToast } from '../services/toast';
import { tempSharedQuotePaymentMethodState } from '../store/global.store';
import { getRecordShareErrorLogContext } from '../utils/recordShareLogging';
import { showMicrodepositVerificationToast } from '../utils/stripe';
import { apiGet, apiPost, apiPut } from './axios';
import { invoiceServiceQueryKeys } from './invoiceService';

export const shareQueryKeys = {
  base: () => ['share'] as const,
  baseWithSecret: (secretId: string) =>
    [...shareQueryKeys.base(), secretId] as const,
  engagement: (recordId: string) =>
    [...shareQueryKeys.base(), 'engagement', recordId] as const,
  invoice: (secretId: string) =>
    [...shareQueryKeys.baseWithSecret(secretId), 'invoice'] as const,
  quote: (secretId: string) =>
    [...shareQueryKeys.baseWithSecret(secretId), 'quote'] as const,
  quotePdf: (secretId: string) =>
    [...shareQueryKeys.quote(secretId), 'pdf'] as const,
  paymentMethods: (secretId: string) =>
    [...shareQueryKeys.baseWithSecret(secretId), 'paymentMethods'] as const,
};

export function useGetRecordShareEngagements<
  SelectData = RecordShareEngagement[],
>(
  recordId: string,
  options: Partial<
    UseQueryOptions<RecordShareEngagement[], unknown, SelectData>
  > = {},
) {
  return useQuery<RecordShareEngagement[], unknown, SelectData>({
    queryKey: shareQueryKeys.engagement(recordId),
    queryFn: () =>
      apiGet<RecordShareEngagement[]>(`/api/share/engagement/${recordId}`).then(
        (res) => RecordShareEngagementSchema.array().parse(res.data),
      ),
    ...options,
  });
}

export function useGetInvoiceByShareId(
  secretId: string,
  tenantId: string,
  options: Partial<UseQueryOptions<IShareInvoiceRespSchema>> = {},
) {
  return useQuery({
    queryKey: [...shareQueryKeys.invoice(secretId)],
    queryFn: () =>
      apiGet<IShareInvoiceRespSchema>(`/api/share/invoices/${secretId}`, {
        axiosConfig: {
          excludeUserIdFromHeader: true,
          excludeTenantIdFromHeader: true,
          customXTenantId: tenantId,
        },
      }).then((res) => res.data),
    ...options,
    refetchOnWindowFocus: false,
  });
}

export function useGetPaymentMethodsByInvoiceShareId<
  SelectData = Array<PaymentMethodSummaryResponse>,
>(
  { invoiceSecretId, tenantId }: { invoiceSecretId: string; tenantId: string },
  options: Partial<
    UseQueryOptions<Array<PaymentMethodSummaryResponse>, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...shareQueryKeys.paymentMethods(invoiceSecretId)],
    queryFn: () =>
      apiGet<Array<PaymentMethodSummaryResponse>>(
        `/api/share/invoices/${invoiceSecretId}/paymentMethods`,
        {
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data),
    ...options,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useSetupIntentForInvoiceShare(
  options: Partial<
    UseMutationOptions<
      IShareInvoiceGenerateSetupIntentResp,
      unknown,
      { invoiceSecretId: string; tenantId: string }
    >
  > = {},
) {
  return useMutation<
    IShareInvoiceGenerateSetupIntentResp,
    unknown,
    { invoiceSecretId: string; tenantId: string }
  >({
    mutationFn: ({ invoiceSecretId, tenantId }) => {
      return apiPost<IShareInvoiceGenerateSetupIntentResp>(
        `/api/share/invoices/${invoiceSecretId}/paymentMethods/token`,
        null,
        {
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data);
    },
    ...options,
  });
}

export const doPrintInvoicePdfForInvoiceShare = async (
  tenantId: string,
  invoiceSecretId: string,
  invoiceId: string,
  invoiceNumber: Maybe<string>,
) => {
  const res = await apiGet<any>(
    `/api/share/invoices/${invoiceSecretId}/print`,
    {
      responseType: 'arraybuffer',
      axiosConfig: {
        excludeUserIdFromHeader: true,
        excludeTenantIdFromHeader: true,
        customXTenantId: tenantId,
      },
      headers: new AxiosHeaders({
        accept: 'application/pdf',
      }),
    },
  );
  return {
    data: res.data,
    fileName: `invoice-${invoiceNumber || invoiceId}.pdf`,
  };
};

export const usePrintInvoiceToHtmlForInvoiceShare = (
  {
    invoiceSecretId,
    lastModifiedTimestamp,
    tenantId,
  }: {
    invoiceSecretId: string;
    /** Used for caching response */
    lastModifiedTimestamp?: string;
    tenantId: string;
  },
  options: Partial<UseQueryOptions<string>> = {},
) => {
  return useQuery({
    queryKey: [
      ...invoiceServiceQueryKeys.htmlTemplate(invoiceSecretId),
      lastModifiedTimestamp,
      tenantId,
    ],
    queryFn: () =>
      apiGet<string>(`/api/share/invoices/${invoiceSecretId}/print`, {
        responseType: 'text',
        headers: new AxiosHeaders({
          accept: 'text/html',
        }),
        axiosConfig: {
          excludeUserIdFromHeader: true,
          excludeTenantIdFromHeader: true,
          customXTenantId: tenantId,
        },
      }).then((res) => res.data),

    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData, previousQuery) => previousData,
    ...options,
  });
};

export function useInvoicePaymentForInvoiceShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        invoiceSecretId: string;
        tenantId: string;
        body: ISharedInvoicePaymentReq;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    {
      invoiceSecretId: string;
      tenantId: string;
      body: ISharedInvoicePaymentReq;
    }
  >({
    mutationFn: ({ invoiceSecretId, tenantId, body }) => {
      return apiPost(
        `/api/share/invoices/${invoiceSecretId}/startPayment`,
        body,
        {
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => {
        queryClient.invalidateQueries({
          queryKey: shareQueryKeys.invoice(invoiceSecretId),
        });
        queryClient.invalidateQueries({
          queryKey: invoiceServiceQueryKeys.htmlTemplate(invoiceSecretId),
        });
        return res.data;
      });
    },
    ...options,
  });
}

/**
 * Create payment method for invoice share
 * this is used when the payment method has additional action required
 * such as verifying micro-deposits
 */
export function useCreateInvoicePaymentMethodForInvoiceShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        invoiceSecretId: string;
        tenantId: string;
        body: ISharedInvoicePaymentReq;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    {
      invoiceSecretId: string;
      tenantId: string;
      body: ISharedInvoicePaymentReq;
    }
  >({
    mutationFn: ({ invoiceSecretId, tenantId, body }) => {
      return apiPost(
        `/api/share/invoices/${invoiceSecretId}/paymentMethods`,
        body,
        {
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => {
        queryClient.invalidateQueries({
          queryKey: shareQueryKeys.invoice(invoiceSecretId),
        });
        queryClient.invalidateQueries({
          queryKey: invoiceServiceQueryKeys.htmlTemplate(invoiceSecretId),
        });
        return res.data;
      });
    },
    ...options,
  });
}

/**
 * Pay with existing payment method
 */
export function usePayInvoiceWithPaymentMethodForInvoiceShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        invoiceSecretId: string;
        tenantId: string;
        body: SharedInvoicePaymentExistingMethod;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    {
      invoiceSecretId: string;
      tenantId: string;
      body: SharedInvoicePaymentExistingMethod;
    }
  >({
    mutationFn: ({ invoiceSecretId, tenantId, body }) => {
      return apiPost(`/api/share/invoices/${invoiceSecretId}/payment`, body, {
        axiosConfig: {
          excludeUserIdFromHeader: true,
          excludeTenantIdFromHeader: true,
          customXTenantId: tenantId,
        },
      }).then((res) => {
        queryClient.invalidateQueries({
          queryKey: shareQueryKeys.invoice(invoiceSecretId),
        });
        queryClient.invalidateQueries({
          queryKey: invoiceServiceQueryKeys.htmlTemplate(invoiceSecretId),
        });
        return res.data;
      });
    },
    ...options,
  });
}

export function useValidateRecordShare(
  options: Partial<
    UseMutationOptions<
      IShareInvoiceGenerateSetupIntentResp,
      unknown,
      RecordShareBaseParams & { entity?: string }
    >
  > = {},
) {
  return useMutation<
    IShareInvoiceGenerateSetupIntentResp,
    unknown,
    RecordShareBaseParams & { entity?: string }
  >({
    mutationFn: ({ recordId, tenantId, token, entity = 'QUOTE' }) => {
      return apiGet<IShareInvoiceGenerateSetupIntentResp>(
        `/api/share/validate/${entity}/${recordId}`,
        {
          params: { token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data);
    },
    ...options,
  });
}

export function useTrackRecordShareAction(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      RecordShareBaseParams & {
        entity: RecordShareEntity;
        action: RecordShareEngagementAction;
        email?: Maybe<string>;
      }
    >
  > = {},
) {
  return useMutation<
    void,
    unknown,
    RecordShareBaseParams & {
      entity: RecordShareEntity;
      action: RecordShareEngagementAction;
      email?: Maybe<string>;
    }
  >({
    mutationFn: ({ recordId, tenantId, token, entity, action, email }) => {
      return apiPost<void>(
        `/api/share/engagement/${entity}/${recordId}`,
        { action, email },
        {
          params: { token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data);
    },
    ...options,
  });
}

export function useGetQuoteShare<SelectData = QuoteShareResponse>(
  { tenantId, token, recordId }: RecordShareBaseParams,
  options: Partial<
    UseQueryOptions<QuoteShareResponse, unknown, SelectData>
  > = {},
) {
  return useQuery<QuoteShareResponse, unknown, SelectData>({
    queryKey: [...shareQueryKeys.quote(recordId), tenantId, token],
    queryFn: () =>
      apiGet<QuoteShareResponse>(`/api/share/quotes/${recordId}`, {
        params: { token },
        axiosConfig: {
          excludeUserIdFromHeader: true,
          excludeTenantIdFromHeader: true,
          customXTenantId: tenantId,
        },
      }).then((res) => res.data),
    ...options,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function doPrintQuoteShare(
  quoteId: string,
  token: string,
  tenantId: string,
) {
  return apiGet<ArrayBuffer>(
    `/api/share/quotes/${quoteId}/print?getSignedIfAvailable=true`,
    {
      params: { token },
      headers: new AxiosHeaders({
        Accept: 'application/pdf',
      }),
      responseType: 'arraybuffer',
      axiosConfig: {
        excludeUserIdFromHeader: true,
        excludeTenantIdFromHeader: true,
        customXTenantId: tenantId,
      },
    },
  );
}

export function usePrintQuoteShare(
  { tenantId, token, recordId }: RecordShareBaseParams,
  options: Partial<UseQueryOptions<ArrayBuffer>> = {},
) {
  return useQuery<ArrayBuffer>({
    queryKey: [...shareQueryKeys.quotePdf(recordId), tenantId, token],
    queryFn: () =>
      doPrintQuoteShare(recordId, token, tenantId).then((res) => res.data),
    ...options,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) => previousData,
  });
}

export function useSetupIntentForQuoteShare(
  options: Partial<
    UseMutationOptions<
      IShareInvoiceGenerateSetupIntentResp,
      unknown,
      RecordShareBaseParams & { paymentGatewayId: string }
    >
  > = {},
) {
  return useMutation<
    IShareInvoiceGenerateSetupIntentResp,
    unknown,
    RecordShareBaseParams & { paymentGatewayId: string }
  >({
    mutationFn: ({ recordId, tenantId, token, paymentGatewayId }) => {
      return apiPost<IShareInvoiceGenerateSetupIntentResp>(
        `/api/share/quotes/${recordId}/paymentMethods/token`,
        { paymentGatewayId },
        {
          params: { token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data);
    },
    ...options,
  });
}

/**
 * Build the contact payload for a quote-share contact update.
 *
 * If a contact's email address was changed we treat it as a brand-new contact
 * (id cleared) rather than editing the existing one. We also never re-use the
 * billing contact for shipping once the user splits them apart.
 */
export function resolveQuoteShareContactRequest(
  originalBillingContact: { id?: Maybe<string>; email: string },
  originalShippingContact: { id?: Maybe<string>; email: string },
  contacts: QuoteShareContactsForm,
): QuoteShareContactRequest {
  const billingContact: QuoteShareContact = { ...contacts.billingContact };
  if (billingContact.email.trim() !== originalBillingContact.email.trim()) {
    billingContact.id = null;
  }

  let shippingContact: QuoteShareContact;
  if (contacts.useBillingForShippingContact) {
    shippingContact = billingContact;
  } else {
    shippingContact = { ...contacts.shippingContact };
    if (
      // If the contacts were the same to begin with, never re-use the billing contact once split
      shippingContact.id === billingContact.id ||
      // Email changed, so assume this is a new contact rather than an edit
      shippingContact.email.trim() !== originalShippingContact.email.trim()
    ) {
      shippingContact.id = null;
    }
  }

  return { billingContact, shippingContact };
}

/**
 * Standalone hook for saving contacts from the contact edit modal.
 * Updates contacts via the share API and updates the cached quote data.
 */
export function useUpdateQuoteShareContacts(
  options: Partial<
    UseMutationOptions<
      QuoteShareResponse,
      unknown,
      {
        authParams: RecordShareBaseParams;
        quoteShareData: QuoteShareResponse;
        contacts: QuoteShareContactsForm;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    QuoteShareResponse,
    unknown,
    {
      authParams: RecordShareBaseParams;
      quoteShareData: QuoteShareResponse;
      contacts: QuoteShareContactsForm;
    }
  >({
    mutationFn: async ({
      authParams: { recordId, tenantId, token },
      quoteShareData,
      contacts,
    }) => {
      const contactRequest = resolveQuoteShareContactRequest(
        quoteShareData.quote.billingContact,
        quoteShareData.quote.shippingContact,
        contacts,
      );

      const response = await apiPut<QuoteShareResponse>(
        `/api/share/quotes/${recordId}/contacts`,
        contactRequest,
        {
          params: { token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      );
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const {
        authParams: { recordId, tenantId, token },
      } = variables;
      // Update cached quote data so the UI reflects the new contacts
      queryClient.setQueryData(
        [...shareQueryKeys.quote(recordId), tenantId, token],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quote(recordId), tenantId, token],
      });
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quotePdf(recordId), tenantId, token],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdatePoNumberOnQuoteShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        authParams: RecordShareBaseParams;
        purchaseOrderNumber: string | null;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    {
      authParams: RecordShareBaseParams;
      purchaseOrderNumber: string | null;
    }
  >({
    mutationFn: ({ authParams, purchaseOrderNumber }) =>
      apiPut(
        `/api/share/quotes/${authParams.recordId}/purchaseOrderNumber`,
        { purchaseOrderNumber },
        {
          params: { token: authParams.token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: authParams.tenantId,
          },
        },
      ).then((res) => res.data),
    ...options,
    onSuccess: (data, variables, context) => {
      const {
        authParams: { recordId, tenantId, token },
      } = variables;
      // Update cached quote data so the UI reflects the new contacts
      const priorData = queryClient.getQueryData([
        ...shareQueryKeys.quote(recordId),
        tenantId,
        token,
      ]) as QuoteShareResponse | undefined;

      if (priorData) {
        const newData: QuoteShareResponse = {
          ...priorData,
          quote: {
            ...priorData.quote,
            purchaseOrderNumber: variables.purchaseOrderNumber || null,
          },
        };
        queryClient.setQueryData(
          [...shareQueryKeys.quote(recordId), tenantId, token],
          newData,
        );
      }
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quote(recordId), tenantId, token],
      });
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quotePdf(recordId), tenantId, token],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateVatNumberOnQuoteShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        authParams: RecordShareBaseParams;
        vatNumber: string | null;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    {
      authParams: RecordShareBaseParams;
      vatNumber: string | null;
    }
  >({
    mutationFn: ({ authParams, vatNumber }) =>
      apiPut(
        `/api/share/quotes/${authParams.recordId}/vatNumber`,
        { vatNumber },
        {
          params: { token: authParams.token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: authParams.tenantId,
          },
        },
      ).then((res) => res.data),
    ...options,
    onSuccess: (data, variables, context) => {
      const {
        authParams: { recordId, tenantId, token },
      } = variables;
      // Update cached quote data so the UI reflects the new VAT number
      const priorData = queryClient.getQueryData([
        ...shareQueryKeys.quote(recordId),
        tenantId,
        token,
      ]) as QuoteShareResponse | undefined;

      if (priorData) {
        const newData: QuoteShareResponse = {
          ...priorData,
          quote: {
            ...priorData.quote,
            vatNumber: variables.vatNumber || null,
          },
        };
        queryClient.setQueryData(
          [...shareQueryKeys.quote(recordId), tenantId, token],
          newData,
        );
      }
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quote(recordId), tenantId, token],
      });
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quotePdf(recordId), tenantId, token],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateAccountNameOnQuoteShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        authParams: RecordShareBaseParams;
        accountName: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    {
      authParams: RecordShareBaseParams;
      accountName: string;
    }
  >({
    mutationFn: ({ authParams, accountName }) =>
      apiPut(
        `/api/share/quotes/${authParams.recordId}/account`,
        { accountName },
        {
          params: { token: authParams.token },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: authParams.tenantId,
          },
        },
      ).then((res) => res.data),
    ...options,
    onSuccess: (data, variables, context) => {
      const {
        authParams: { recordId, tenantId, token },
      } = variables;
      // Update cached quote data so the UI reflects the new account name
      const priorData = queryClient.getQueryData([
        ...shareQueryKeys.quote(recordId),
        tenantId,
        token,
      ]) as QuoteShareResponse | undefined;

      if (priorData) {
        const newData: QuoteShareResponse = {
          ...priorData,
          quote: {
            ...priorData.quote,
            accountName: variables.accountName,
          },
        };
        queryClient.setQueryData(
          [...shareQueryKeys.quote(recordId), tenantId, token],
          newData,
        );
      }
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quote(recordId), tenantId, token],
      });
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quotePdf(recordId), tenantId, token],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Helper function to confirm Stripe SetupIntent with redirect support
 * This is NOT a mutation hook - just a plain function that returns a promise
 */
export function savePaymentMethodOnStripe({
  stripe,
  elements,
  billingContact,
  returnUrlPath,
}: {
  stripe: Stripe;
  elements: StripeElements;
  billingContact?: IPaymentMethodContactSchema;
  returnUrlPath: string;
}): Promise<SetupIntentResult> {
  return stripe.confirmSetup({
    elements,
    confirmParams: {
      return_url: `${window.location.origin}${returnUrlPath}`,
      payment_method_data: billingContact
        ? {
            billing_details: {
              email: billingContact.email,
              name: billingContact.fullName,
              phone: billingContact.phone,
            },
          }
        : undefined,
    },
    redirect: 'if_required',
  });
}

export function useCreatePaymentMethodForQuoteShare(
  options: Partial<
    UseMutationOptions<
      PaymentMethodSummaryResponse,
      unknown,
      {
        stripe: Stripe;
        elements: StripeElements;
        authParams: RecordShareBaseParams;
        requestData: QuoteSharePaymentMethodRequest;
        returnUrlPath: string;
        stripePublicKey: string;
      }
    >
  > = {},
) {
  const { addToast } = useToast();
  const setTempPaymentMethodData = useSetAtom(
    tempSharedQuotePaymentMethodState,
  );

  // Helper function to check if redirect is needed and handle it
  const handleStripeResult = async (
    stripeResult: SetupIntentResult,
    authParams: RecordShareBaseParams,
    requestData: QuoteSharePaymentMethodRequest,
  ): Promise<PaymentMethodSummaryResponse> => {
    const { error, setupIntent } = stripeResult;
    const { recordId, tenantId, token } = authParams;

    if (error) {
      logger.error('Error saving payment method to stripe', error);
      setTempPaymentMethodData(null);
      throw new Error(error.message);
    }

    // Check if redirect is required
    if (
      setupIntent.status === 'requires_action' ||
      setupIntent.status === 'requires_confirmation'
    ) {
      if (
        setupIntent?.next_action?.type === 'redirect_to_url' &&
        setupIntent.next_action.redirect_to_url?.url
      ) {
        // Manually redirect user to complete authentication (3DS, etc.)
        // Temp state is already stored, will be used after redirect
        window.location.href = setupIntent.next_action.redirect_to_url.url;
        // Return empty response as redirect will happen
        return {} as PaymentMethodSummaryResponse;
      } else if (
        setupIntent?.next_action?.type === 'verify_with_microdeposits'
      ) {
        const verificationUrl =
          setupIntent.next_action.verify_with_microdeposits
            ?.hosted_verification_url;

        if (verificationUrl) {
          showMicrodepositVerificationToast(addToast, verificationUrl);
          return {} as PaymentMethodSummaryResponse;
        }
        setTempPaymentMethodData(null);
        throw new Error('Microdeposit verification required');
      }
    }

    // No redirect needed - complete the payment method creation
    requestData.paymentMethod.paymentToken = setupIntent.id;

    const result = await apiPost<PaymentMethodSummaryResponse>(
      `/api/share/quotes/${recordId}/paymentMethods`,
      requestData,
      {
        params: { token },
        axiosConfig: {
          excludeUserIdFromHeader: true,
          excludeTenantIdFromHeader: true,
          customXTenantId: tenantId,
        },
      },
    );

    setTempPaymentMethodData(null);
    return result.data;
  };

  return useMutation<
    PaymentMethodSummaryResponse,
    unknown,
    {
      stripe: Stripe;
      elements: StripeElements;
      authParams: RecordShareBaseParams;
      requestData: QuoteSharePaymentMethodRequest;
      returnUrlPath: string;
      stripePublicKey: string;
    }
  >({
    mutationFn: ({
      authParams,
      requestData,
      stripe,
      elements,
      returnUrlPath,
      stripePublicKey,
    }) => {
      const { recordId, tenantId, token } = authParams;

      // Store temp state before potential redirect
      setTempPaymentMethodData({
        recordId,
        tenantId,
        token,
        stripePublicKey,
        paymentGatewayId: requestData.paymentGatewayId,
      });

      // Call Stripe to confirm setup - if manual redirect needed, handleStripeResult will do it
      return savePaymentMethodOnStripe({
        billingContact: requestData.paymentMethod.billingDetails,
        stripe,
        elements,
        returnUrlPath,
      }).then((result) => handleStripeResult(result, authParams, requestData));
    },
    ...options,
  });
}

export function useAcceptQuoteForQuoteShare(
  options: Partial<
    UseMutationOptions<
      QuoteShareResponse,
      unknown,
      {
        authParams: RecordShareBaseParams;
        paymentMethodId?: Maybe<string>;
        collectUpfrontPayment?: boolean;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    QuoteShareResponse,
    unknown,
    {
      authParams: RecordShareBaseParams;
      paymentMethodId?: Maybe<string>;
      collectUpfrontPayment?: boolean;
    }
  >({
    mutationFn: async ({
      authParams,
      paymentMethodId,
      collectUpfrontPayment,
    }) => {
      const { recordId, tenantId, token } = authParams;
      // accept quote
      return apiPost<QuoteShareResponse>(
        `/api/share/quotes/${recordId}/acceptAndProcess`,
        null,
        {
          params: { token, paymentMethodId, collectUpfrontPayment },
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) => res.data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const {
        authParams: { recordId, tenantId, token },
      } = variables;
      Sentry.logger.info('useAcceptQuoteForQuoteShare - accept succeeded', {
        quoteId: recordId,
        tenantId,
        hasRedirectUrl: String(!!data?.redirectUrl),
      });
      // Update cached quote data so the UI reflects the new contacts
      queryClient.setQueryData(
        [...shareQueryKeys.quote(recordId), tenantId, token],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quote(recordId), tenantId, token],
      });
      queryClient.invalidateQueries({
        queryKey: [...shareQueryKeys.quotePdf(recordId), tenantId, token],
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const { authParams } = variables;
      const logContext = getRecordShareErrorLogContext(authParams, error);
      Sentry.logger.error('useAcceptQuoteForQuoteShare - accept failed', {
        ...logContext,
        quoteId: authParams.recordId,
      });
      options?.onError?.(error, variables, context);
    },
  });
}
